import { Capacitor } from '@capacitor/core';

/**
 * Wo steht der Server?
 *
 * Im Browser ist die Frage beantwortet, bevor sie jemand stellt: die App wird
 * vom Server selbst ausgeliefert, also sind alle Adressen relativ.
 *
 * In der Android-App nicht. Dort liegt die Oberfläche im APK und wird von
 * Capacitors eingebautem Server unter `https://localhost` ausgeliefert - der
 * kennt den echten Server nicht. Schlimmer noch: unbekannte Pfade beantwortet
 * er mit der `index.html` und Status 200, eine nicht gesetzte Adresse fällt
 * also nicht als Verbindungsfehler auf. Deshalb fragt die App beim ersten
 * Start einmalig danach.
 */

const KEY = 'r2f.server';

/** Nur die verpackte App braucht eine Adresse - im Browser nie. */
export function needsServerAddress(): boolean {
  return Capacitor.isNativePlatform();
}

/** Vorne an jeden API-Pfad. Im Browser leer, also unverändert relativ. */
export function getServerBase(): string {
  if (!needsServerAddress()) return '';
  try {
    return localStorage.getItem(KEY) ?? '';
  } catch {
    return '';
  }
}

export function setServerBase(url: string): void {
  try {
    localStorage.setItem(KEY, url);
  } catch {
    // Ohne Speicher gilt die Adresse eben nur für diese Sitzung.
  }
}

export function clearServerBase(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* egal */
  }
}

/** Die Adresse ist eingerichtet - oder wird gar nicht gebraucht. */
export function isServerConfigured(): boolean {
  return !needsServerAddress() || getServerBase() !== '';
}

/**
 * Was jemand eintippt, ist selten eine vollständige Adresse. Fehlt das
 * Schema, wird nicht geraten, sondern der Reihe nach probiert - erst
 * `https://`, dann `http://`. Andersherum wäre es falsch: ein Server im
 * Internet spricht praktisch immer HTTPS, und wer auf Port 443 lauscht,
 * lauscht meist gar nicht erst auf Port 80.
 */
export function candidateServerUrls(input: string): string[] {
  const trimmed = input.trim().replace(/\/+$/, '');
  if (!trimmed) return [];
  if (/^https?:\/\//i.test(trimmed)) return [trimmed];
  return [`https://${trimmed}`, `http://${trimmed}`];
}
